// Приготвените рецепти в листа „Инвентаризация" — чистата логика (без React).
//
// Ястие, което е приготвено и чака продажба („Шопска салата × 3"), държи
// БРУТОТО на съставките си: то ще се изпише при разнасянето на отчета, в който
// се продаде. Затова инвентаризацията ПРИБАВЯ съставките към преброеното на
// продукта — наличността по книга след нея = насипно + в приготвените ястия.
//
// Сметката е огледало на BE (`service_stocktake_recipes.py`), иначе разликата
// на екрана и в документа се разминават:
//  * съставките — бруто за една порция, под-рецептите разгънати, продукт от
//    няколко места — сумиран (`GET /recipe-ingredients`); вече записана в
//    документа рецепта ползва СНИМКАТА си (`savedIngredients`), не текущата;
//  * частта на продукта в рецептите = Σ бруто × порции, закръглена до 4 знака
//    с половинката нагоре (`roundQuantity`) — колоната е Numeric(13, 4);
//  * в заявката `counted` е НАСИПНОТО, рецептите отиват отделно (`recipes`);
//    BE сам ги прибавя — сборът в `counted` би удвоил съставките.
import type { TFunction } from "i18next";
import type { RecipeIngredient, Stocktake } from "../../../../api/inventory";
import type { CreateStocktakeRecipe } from "../../../../api/inventoryStocktakes";
import { STOCKTAKE_COLS, parseDecimal } from "./stocktakeWorksheetShared";
import type { StocktakeIssue } from "./stocktakeIssues";

// Бруто за ЕДНА порция по продукт
export type RecipePortionIngredient = {
  productId: number;
  productName: string;
  perPortion: number;
};

// Един ред в „Приготвени рецепти" — само въведеното (това е и черновата)
export type StocktakeRecipeEntry = {
  recipeId: number;
  recipeName: string;
  portions: string; // както се пише („2,5")
  // Снимката от записания документ (редакция) — като BE: вече записана
  // рецепта пази разгъването си; null → текущата рецепта
  savedIngredients: RecipePortionIngredient[] | null;
};

// Форматът за черновата (`snapshot()` / `apply()` на useStocktakeRecipes)
export type StocktakeRecipesSnapshot = { v: 1; entries: StocktakeRecipeEntry[] };

export type RecipeIngredientsState =
  | { status: "ready"; ingredients: RecipePortionIngredient[] }
  | { status: "loading" }
  | { status: "error" };

// Какво съдържа рецептата за написаните порции (при празни — за една порция)
export type StocktakeRecipeContent = RecipePortionIngredient & { quantity: number };

export type StocktakeRecipeRow = {
  key: string; // ключът на реда в решетката — „r12" (продуктовите са числа)
  entry: StocktakeRecipeEntry;
  portions: number; // 0 при празно
  ingredients: RecipeIngredientsState;
  contents: StocktakeRecipeContent[];
};

// Една рецепта в разбивката на продукт („Шопска салата × 3 — 0,630")
export type InRecipesPart = {
  recipeId: number;
  recipeName: string;
  portions: number;
  quantity: number;
};
export type InRecipesOfProduct = { total: number; parts: InRecipesPart[] };

// Ключ на ред-рецепта в решетката: id-тата на клетките са `stx-r12-0`, а
// продуктовите — `stx-31-0`; двата вида не се бият
export const recipeRowKey = (recipeId: number) => `r${recipeId}`;
// Полето „Порции" — в колоната на „Намерено", със същата схема на id-то
export const recipePortionsCellId = (recipeId: number) =>
  `stx-${recipeRowKey(recipeId)}-${STOCKTAKE_COLS.counted}`;

// 4 знака, половинката нагоре — както BE (Numeric(13, 4), ROUND_HALF_UP).
// `toFixed(6)` маха шума на двоичната запетая (1,00005 × 10000 = 10000,4999…).
export const roundQuantity = (value: number) =>
  Math.round(Number((value * 10000).toFixed(6))) / 10000;

// Порциите в полето: „3", „2,5" — без излишни нули
export const formatPortionsInput = (value: number) => String(roundQuantity(value)).replace(".", ",");

// `GET /recipe-ingredients` → бруто за порция по продукт (BE вече ги сумира;
// групирането е за сигурност). Нулевите съставки нищо не прибавят. Сборът се
// чисти от шума на двоичната запетая (0,2 + 0,01 = 0,21, не 0,21000000000000002)
// БЕЗ да се реже до 4 знака — BE пази брутото за порция точно.
export const portionIngredientsOf = (ingredients: RecipeIngredient[]): RecipePortionIngredient[] => {
  const byProduct = new Map<number, RecipePortionIngredient>();
  for (const ingredient of ingredients) {
    const current = byProduct.get(ingredient.productId);
    byProduct.set(ingredient.productId, {
      productId: ingredient.productId,
      productName: ingredient.productName,
      perPortion: Number(((current?.perPortion ?? 0) + (ingredient.quantity || 0)).toFixed(10)),
    });
  }
  return [...byProduct.values()].filter((ingredient) => ingredient.perPortion > 0);
};

// Записаните рецепти на документа → редове на листа (снимката остава)
export const entriesOfStocktake = (stocktake: Stocktake): StocktakeRecipeEntry[] =>
  (stocktake.recipes ?? []).map((recipe) => ({
    recipeId: recipe.recipeId,
    recipeName: recipe.recipeName,
    portions: formatPortionsInput(recipe.portions),
    savedIngredients: recipe.ingredients.map((ingredient) => ({
      productId: ingredient.productId,
      productName: ingredient.productName,
      perPortion: ingredient.perPortion,
    })),
  }));

export const buildRecipeRows = (
  entries: StocktakeRecipeEntry[],
  stateOf: (entry: StocktakeRecipeEntry) => RecipeIngredientsState,
): StocktakeRecipeRow[] =>
  entries.map((entry) => {
    const portions = Math.max(0, parseDecimal(entry.portions));
    const ingredients: RecipeIngredientsState = entry.savedIngredients
      ? { status: "ready", ingredients: entry.savedIngredients }
      : stateOf(entry);
    const multiplier = portions > 0 ? portions : 1;
    return {
      key: recipeRowKey(entry.recipeId),
      entry,
      portions,
      ingredients,
      contents:
        ingredients.status === "ready"
          ? ingredients.ingredients.map((ingredient) => ({
              ...ingredient,
              quantity: ingredient.perPortion * multiplier,
            }))
          : [],
    };
  });

// {продукт: колко от него е в приготвените рецепти + разбивката по рецепта}.
// Редът на ключовете е редът на рецептите и съставките им — в него съставките,
// които още не са преброени насипно, застават в „За броене".
export const recipeQuantitiesByProduct = (rows: StocktakeRecipeRow[]): Map<number, InRecipesOfProduct> => {
  const raw = new Map<number, { total: number; parts: InRecipesPart[] }>();
  for (const row of rows) {
    if (row.portions <= 0 || row.ingredients.status !== "ready") continue;
    for (const ingredient of row.ingredients.ingredients) {
      const quantity = ingredient.perPortion * row.portions;
      const current = raw.get(ingredient.productId) ?? { total: 0, parts: [] };
      current.total += quantity;
      current.parts.push({
        recipeId: row.entry.recipeId,
        recipeName: row.entry.recipeName,
        portions: row.portions,
        quantity,
      });
      raw.set(ingredient.productId, current);
    }
  }
  return new Map(
    [...raw.entries()].map(([productId, value]) => [
      productId,
      { total: roundQuantity(value.total), parts: value.parts },
    ]),
  );
};

// Тялото на заявката — порциите с 4 знака, както ги пази BE
export const recipesPayloadOf = (entries: StocktakeRecipeEntry[]): CreateStocktakeRecipe[] =>
  entries.map((entry) => ({
    recipeId: entry.recipeId,
    portions: roundQuantity(parseDecimal(entry.portions)),
  }));

const LISTED_NAMES = 3;
const listNames = (names: string[], t: TFunction) => {
  const shown = names.slice(0, LISTED_NAMES).join(", ");
  const more = names.length - LISTED_NAMES;
  return more > 0 ? `${shown} ${t("inventoryPage.stkIssueMore", { count: more })}` : shown;
};

type RecipeIssuesParams = {
  rows: StocktakeRecipeRow[];
  // съставките, които още нямат насипно преброено (в реда на рецептите)
  uncountedIngredients: { productId: number; name: string }[];
  t: TFunction;
};

// Пречките на рецептите пред записа — по реда: порции · рецепта без съставки ·
// съставка без насипно преброено (иначе целият ѝ насипен запас става липса)
export const buildRecipeIssues = ({ rows, uncountedIngredients, t }: RecipeIssuesParams): StocktakeIssue[] => {
  const issues: StocktakeIssue[] = [];
  const withoutPortions = rows.filter((row) => row.portions <= 0);
  if (withoutPortions.length)
    issues.push({
      text: t("inventoryPage.sr6IssuePortions", {
        recipes: listNames(withoutPortions.map((row) => row.entry.recipeName), t),
      }),
      focusRecipeId: withoutPortions[0].entry.recipeId,
    });
  for (const row of rows) {
    if (row.ingredients.status !== "ready" || row.ingredients.ingredients.length > 0) continue;
    issues.push({
      text: t("inventoryPage.sr6IssueEmptyRecipe", { name: row.entry.recipeName }),
      focusRecipeId: row.entry.recipeId,
    });
  }
  if (uncountedIngredients.length)
    issues.push({
      text: t("inventoryPage.sr6IssueCountLoose", {
        products: listNames(uncountedIngredients.map((item) => item.name), t),
      }),
      focus: { productId: uncountedIngredients[0].productId, col: STOCKTAKE_COLS.counted },
    });
  return issues;
};
